import { useState } from 'react';
import { useStatsOverview, useStatsScenes, useStatsRetention, useStatsSkillLevel, useStatsMasteryCurve } from '@/api/use-stats';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell,
} from 'recharts';
import { Download, TrendingUp, TrendingDown, Minus, Star, Clock, Target, AlertTriangle, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';


const COLORS = {
  primary: '#6366f1',
  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
  blue: '#3b82f6',
  pink: '#ec4899',
};

const TIME_RANGES = [
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
  { value: 180, label: '180 Days' },
];

function TrendIcon({ value }: { value: number }) {
  if (value > 0) return <span className="text-emerald-500 text-xs font-medium flex items-center gap-0.5"><TrendingUp className="w-3 h-3" />+{value}%</span>;
  if (value < 0) return <span className="text-red-500 text-xs font-medium flex items-center gap-0.5"><TrendingDown className="w-3 h-3" />{value}%</span>;
  return <span className="text-muted-foreground text-xs font-medium flex items-center gap-0.5"><Minus className="w-3 h-3" />0%</span>;
}

function KpiCard({ title, value, subtitle, trend, color }: {
  title: string; value: string | number; subtitle?: string; trend?: number; color?: string;
}) {
  return (
    <Card className="rounded-xl">
      <CardContent className="p-5">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</p>
        <div className="flex items-baseline gap-2 mt-1">
          <span className={cn('text-3xl font-bold', color)}>{value}</span>
          {trend != null && <TrendIcon value={trend} />}
        </div>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function MiniStat({ icon: Icon, label, value, color }: {
  icon: React.ElementType; label: string; value: string | number; color?: string;
}) {
  return (
    <Card className="rounded-xl">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', color ?? 'bg-muted')}>
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="text-lg font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function computeSkillLevel(items: { new: number; learning: number; recognized: number; mastered: number }): string {
  const total = items.new + items.learning + items.recognized + items.mastered;
  if (total === 0) return 'A1.1';
  // Weighted mastery ratio — matches backend algorithm
  const masteryRatio = (items.mastered + items.recognized * 0.5 + items.learning * 0.2) / total;
  // CEFR level boundaries
  const levels: Array<{ name: string; min: number; max: number }> = [
    { name: 'A1', min: 0, max: 0.15 },
    { name: 'A2', min: 0.15, max: 0.3 },
    { name: 'B1', min: 0.3, max: 0.5 },
    { name: 'B2', min: 0.5, max: 0.7 },
    { name: 'C1', min: 0.7, max: 0.85 },
    { name: 'C2', min: 0.85, max: 1.0 },
  ];
  let levelInfo = levels[0];
  for (const l of levels) {
    if (masteryRatio >= l.min) levelInfo = l;
  }
  const bandWidth = levelInfo.max - levelInfo.min;
  const progress = bandWidth > 0 ? Math.min((masteryRatio - levelInfo.min) / bandWidth, 0.99) : 0;
  const sub = Math.floor(progress * 9) + 1;
  return `${levelInfo.name}.${sub}`;
}

export default function Stats() {
  const [days, setDays] = useState(60);
  const { data: overview } = useStatsOverview();
  const { data: scenes } = useStatsScenes();
  const { data: retention } = useStatsRetention(days);
  const { data: skillData } = useStatsSkillLevel();
  const { data: masteryCurve } = useStatsMasteryCurve(days);

  const items = overview?.items ?? { new: 0, learning: 0, recognized: 0, mastered: 0 };
  const totalItems = overview?.total_items ?? 0;
  const streak = overview?.streak ?? 0;
  const overdue = retention?.overdue_count ?? 0;
  const totalActive = retention?.total_active ?? 1;
  const retentionPct = totalActive > 0 ? Math.round(((totalActive - overdue) / totalActive) * 100) : 0;
  const dueTodayEstimate = overview?.due_items_count ?? 0;
  const skillLevel = skillData?.level ?? computeSkillLevel(items);

  // Mastery curve from real API
  const masteryData = (masteryCurve ?? []).map((d) => ({
    date: d.date.slice(5), // MM-DD
    mastered: d.mastered,
    learning: d.learning,
  }));

  // Scene proficiency
  const sceneData = (scenes ?? [])
    .filter(s => s.avg_score != null)
    .slice(0, 5)
    .map(s => ({
      name: s.scene_name,
      score: Math.round((s.avg_score ?? 0) * 10) / 10, // 1-10 scale, 1 decimal
      practiced: s.practice_count,
    }));

  // Radar data
  const dims = skillData?.dimensions ?? {};
  const radarData = [
    { dimension: 'Fluency', value: dims.fluency ?? 0 },
    { dimension: 'Vocab', value: dims.vocab ?? 0 },
    { dimension: 'Naturalness', value: dims.naturalness ?? 0 },
    { dimension: 'Interaction', value: dims.interaction ?? 0 },
    { dimension: 'Progression', value: dims.progression ?? 0 },
    { dimension: 'Repair', value: dims.repair ?? 0 },
  ];

  // SM-2 donut — due_items_count = next_review <= today (includes overdue)
  // overdue = next_review < today (strictly past)
  // dueExactlyToday = due_items_count - overdue (items due today only)
  const dueExactlyToday = Math.max(0, dueTodayEstimate - overdue);
  const onSchedule = Math.max(0, totalActive - dueTodayEstimate);
  const sm2Data = [
    { name: 'On Schedule', value: onSchedule, fill: COLORS.blue },
    { name: 'Due Today', value: dueExactlyToday, fill: COLORS.warning },
    { name: 'Overdue', value: overdue, fill: COLORS.error },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-8">
      {/* Time range tabs + Export */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          {TIME_RANGES.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setDays(value)}
              className={cn(
                'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                days === value ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Export Full Report
        </Button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Total Items" value={totalItems} subtitle="words/phrases" trend={overview?.pct_changes?.items_7d} />
        <KpiCard title="Mastered" value={items.mastered} color="text-emerald-600" trend={overview?.pct_changes?.mastered_7d} />
        <KpiCard title="Learning" value={items.learning + items.recognized} subtitle="learning + recognized" />
        <Card className="rounded-xl bg-gradient-to-br from-indigo-600 to-purple-700 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80">Current Streak</p>
              <Badge className="bg-white/20 text-white text-[10px]">Best: {overview?.best_streak ?? 0}d</Badge>
            </div>
            <p className="text-4xl font-bold mt-1">{streak} <span className="text-lg font-normal">DAYS</span></p>
          </CardContent>
        </Card>
      </div>

      {/* Mini Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniStat icon={Star} label="Avg Score" value={overview?.avg_score != null ? `${overview.avg_score.toFixed(1)} / 10` : '\u2014'} />
        <MiniStat icon={Clock} label="Study Time" value={overview?.study_time_hours != null ? `${overview.study_time_hours.toFixed(1)} hrs` : '\u2014'} />
        <MiniStat icon={Target} label="Sessions" value={overview?.sessions_count != null ? `${overview.sessions_count} total` : '\u2014'} />
        <Card className={cn('rounded-xl', overdue > 0 ? 'border-red-200' : '')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                Overdue
                {overdue > 0 && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{overdue}</Badge>}
              </p>
              <p className="text-lg font-bold text-red-600">{overdue} items</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {totalItems === 0 ? (
        <Card className="rounded-xl">
          <CardContent className="py-16 text-center">
            <Target className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-lg font-semibold mb-1">No data yet</h3>
            <p className="text-sm text-muted-foreground">Start adding language items and practicing to see your progress statistics here.</p>
          </CardContent>
        </Card>
      ) : (
      <>
      {/* Charts Row 1: Mastery Curve + Scene Proficiency */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">Mastery Curve</CardTitle>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Mastered</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />Learning</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={masteryData.length > 0 ? masteryData : [{ date: '', mastered: 0, learning: 0 }]}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="mastered" stroke={COLORS.success} strokeWidth={2} dot={false} name="Mastered" />
                <Line type="monotone" dataKey="learning" stroke={COLORS.blue} strokeWidth={2} dot={false} name="Learning" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="rounded-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">Scene Proficiency</CardTitle>
              <span className="text-xs text-muted-foreground">Sessions vs Avg Score</span>
            </div>
          </CardHeader>
          <CardContent>
            {sceneData.length === 0 ? (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">No scene data yet</div>
            ) : (
              <div className="space-y-4">
                {sceneData.map((scene) => (
                  <div key={scene.name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{scene.name}</span>
                      <span className="text-xs text-muted-foreground">{scene.score}/10 · {scene.practiced} sessions</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          scene.score >= 8 ? 'bg-emerald-500' : scene.score >= 6 ? 'bg-indigo-500' : 'bg-amber-500'
                        )}
                        style={{ width: `${Math.min(scene.score * 10, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Radar + SM-2 Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">Capability Radar</CardTitle>
              <Button variant="ghost" size="icon" className="w-6 h-6"><Sparkles className="w-3.5 h-3.5" /></Button>
            </div>
          </CardHeader>
          <CardContent className="relative">
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar dataKey="value" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.25} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
            {/* Center label */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase">Overall Skill</p>
                <p className="text-2xl font-bold text-primary">{skillLevel}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">SM-2 Spaced Repetition Health</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="relative w-[180px] h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sm2Data}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {sm2Data.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">{retentionPct}%</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Retention</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className="text-sm">On Schedule</span>
                  <span className="font-bold ml-auto">{onSchedule}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-sm">Due Today</span>
                  <span className="font-bold ml-auto">{dueExactlyToday}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm">Overdue</span>
                  <span className="font-bold text-red-600 ml-auto">{overdue}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {totalActive > 0 ? (
                    overdue > 0
                      ? `You have ${overdue} overdue items (${100 - retentionPct}% of active). Review them to improve retention.`
                      : `Great consistency! Your retention is at ${retentionPct}%. Keep up the daily practice.`
                  ) : 'Start adding items and practicing to see your retention health.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      </>
      )}

      {/* AI Insight Banner */}
      <Card className="rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <CardContent className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold">AI Insight: Master the Weekend Gap</h3>
              <p className="text-sm opacity-90 mt-0.5">
                Your retention drops by 8% on Mondays. We recommend a 10-minute Sunday "Flash Review" session.
              </p>
            </div>
          </div>
          <button
            className="shrink-0 border border-white/60 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-white/10 transition-colors"
            onClick={() => {
              navigator.clipboard.writeText('/cadence review');
            }}
          >
            Schedule Reminder
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
